import { parseDateToGivenFormat } from '@/utils/dateUtils';

const REJECTED_OFFER_STATUSES = ['rejected', 'rejected_by_candidate'];

interface AdditionalCompensationDefaults {
  amount?: string | null;
  strike_price?: string;
  unit_of_cliff?: string;
  type_of_equity?: string;
  cliff_of_vesting?: string;
  period_of_vesting?: string;
  cycles_after_cliff?: string;
  strike_price_currency?: string | null;
  unit_of_vesting_period?: string;
  unit_of_cycle_after_cliff?: string;
}

export const getInitializeOfferValues = ({
  contactData,
  currentOffer,
  currentJob,
  currUser,
  type,
  additionalCompensationDefaults = {},
}: {
  contactData?: any;
  currentOffer?: any;
  currentJob?: any;
  currUser?: any;
  type?: 'create' | 'edit';
  additionalCompensationDefaults?: AdditionalCompensationDefaults;
}) => {
  const { name = '', email } = contactData || {};
  const { basic_info, name: jobName } = currentJob || {};

  const {
    candidate_name,
    candidate_email,
    candidate_phone = '',
    candidate_address = '',
    salary_currency,
    salary = '',
    ats_equity_award,
    reporting_manager,
    department,
    office,
    additional_compensation = '',
    additional_compensation_currency,
    ats_offer_template,
    additional_info,
    today_date,
    start_date,
    expiration_date,
    salary_frequency,
    job_title,
    req_id,
  } = currentOffer || {};

  const {
    strike_price_currency,
    unit_of_cliff,
    unit_of_cycle_after_cliff,
    unit_of_vesting_period,
    amount = '',
    currency,
  } = ats_equity_award || {};
  const ALLOWED_REQUISITION_STATUSES = ['approved', 'created', 'open'];
  const { basic_info: { requisitions = [] } = {} } = currentJob || {};
  const userHasReqEnabled = currUser?.requisition?.enabled;

  const filteredRequisitions =
    requisitions?.filter(
      (req) =>
        ALLOWED_REQUISITION_STATUSES.includes(req?.status) &&
        !req?.has_association
    ) || [];

  const isRejectedWithNoLinkedReq =
    currentOffer?.id &&
    !currentOffer?.requisition?.id &&
    REJECTED_OFFER_STATUSES.includes(currentOffer?.offer_status) &&
    userHasReqEnabled;

  const JobAssociatedRequisition = isRejectedWithNoLinkedReq
    ? undefined
    : userHasReqEnabled
      ? filteredRequisitions[0]?.code
      : basic_info?.requisitions?.[0]?.requisition_value;

  // Apply additional compensation defaults only when type is 'create'
  const createTypeDefaults =
    type === 'create'
      ? {
          unit_of_cliff: additionalCompensationDefaults.unit_of_cliff || 'year',
          unit_of_cycle_after_cliff:
            additionalCompensationDefaults.unit_of_cycle_after_cliff || 'month',
          unit_of_vesting_period:
            additionalCompensationDefaults.unit_of_vesting_period || 'year',
          cliff_of_vesting: Number(
            additionalCompensationDefaults.cliff_of_vesting
          ),
          period_of_vesting: Number(
            additionalCompensationDefaults.period_of_vesting
          ),
          cycles_after_cliff: Number(
            additionalCompensationDefaults.cycles_after_cliff
          ),
          strike_price: Number(additionalCompensationDefaults.strike_price),
          type_of_equity: additionalCompensationDefaults.type_of_equity,
          ...(additionalCompensationDefaults.amount !== null &&
            additionalCompensationDefaults.amount !== undefined && {
              amount: Number(additionalCompensationDefaults.amount),
            }),
          strike_price_currency_id:
            Number(additionalCompensationDefaults.strike_price_currency) ||
            Number(strike_price_currency?.id) ||
            5,
        }
      : {};

  const defaults = {
    job_title: job_title || jobName,
    candidate_name: candidate_name || name,
    candidate_email: candidate_email || email,
    candidate_phone,
    candidate_address,
    salary_currency_id: salary_currency?.id || 5,
    strike_price_currency_id: strike_price_currency?.id || 5,
    today_date: today_date,
    ats_department_id: department?.id || basic_info?.department?.id,
    ats_office_id: office?.id || basic_info?.office?.[0]?.id,
    additional_compensation_currency_id:
      additional_compensation_currency?.id || 5,
    reporting_manager_id: reporting_manager?.id,
    unit_of_cliff: unit_of_cliff || 'year',
    unit_of_cycle_after_cliff: unit_of_cycle_after_cliff || 'month',
    unit_of_vesting_period: unit_of_vesting_period || 'year',
    award_type: currency?.id ? 'money' : 'units',
    currency_id: currency?.id || 5,
    salary: salary?.split('.')?.[0],
    ...(amount !== null &&
      amount !== undefined && { amount: amount?.split('.')?.[0] }),
    additional_compensation: additional_compensation?.split('.')?.[0],
    start_date,
    expiration_date,
    salary_frequency,
    req_id: isRejectedWithNoLinkedReq
      ? undefined
      : req_id || JobAssociatedRequisition,
    ...createTypeDefaults,
  };

  const details = {
    ...ats_equity_award,
    ...defaults,
    //adding custom fields default value
    ...additional_info?.reduce((acc, { field_id, value, ext_data }) => {
      // Prefer ext_data when BE preserves the verbatim user string (added in
      // core PRs 4861/4864 for number fields). The Decimal-typed `value` would
      // otherwise truncate EU thousand-separator inputs like "1.234.567" to
      // 1.234 on reopen.
      acc[`custom_${field_id}`] = ext_data ?? value;
      return acc;
    }, {}),
  };

  return {
    details,
    templates: {
      ats_offer_template_id: ats_offer_template?.id,
      ats_offer_template: ats_offer_template || {},
    },
  };
};

// File custom fields must go to the backend as a signed blob id (string) or an
// existing attachment id (integer) — never the raw file object. New uploads are
// already converted to signed_id strings before this runs (via
// processFormFilesForUpload at the submit site); here we only unwrap EXISTING
// file objects (which come back from the API carrying `signed_id`/`id` +
// `url`/`filename`). This mirrors how requisitions builds `additional_info`.
// Genuine multi-value arrays (e.g. multi-selects) are left untouched.
const isExistingFileObject = (item: any): boolean =>
  !!item &&
  typeof item === 'object' &&
  ('signed_id' in item || 'id' in item) &&
  ('url' in item || 'filename' in item);

const normalizeCustomFieldValue = (value: any) => {
  if (!Array.isArray(value)) return value;
  if (!value.some(isExistingFileObject)) return value;

  const fileIds = value
    .map((item) =>
      typeof item === 'string' ? item : item?.signed_id || item?.id
    )
    .filter(Boolean);

  if (fileIds.length === 0) return undefined;
  return fileIds.length === 1 ? fileIds[0] : fileIds;
};

function generateCustomPayload(obj) {
  const customObject = {};
  const payload = {};

  for (const [key, value] of Object.entries(obj)) {
    if (key.startsWith('custom_')) {
      const newKey = key.replace('custom_', '');
      const normalized = normalizeCustomFieldValue(value);
      if (normalized !== undefined) {
        customObject[newKey] = normalized;
      }
    } else {
      payload[key] = value;
    }
  }

  return {
    customObject,
    payload,
  };
}

export const generateOfferPayload = ({
  values,
  applicationId,
  offerId: _offerId = 0,
}) => {
  const {
    cliff_of_vesting,
    cycles_after_cliff,
    date_of_grant,
    period_of_vesting,
    start_date_of_vesting,
    strike_price,
    type_of_equity,
    unit_of_cliff,
    unit_of_cycle_after_cliff,
    unit_of_vesting_period,
    amount,
    currency_id,
    award_type,
    today_date,
    start_date,
    expiration_date,
    strike_price_currency_id,
    useLegacy,
    ...otherValues
  } = values || {};

  const { customObject = {}, payload = {} } =
    generateCustomPayload(otherValues);

  if (Object.keys(customObject)?.length > 0) {
    payload['additional_info'] = customObject;
  }

  if (useLegacy !== undefined) {
    payload['use_legacy_flow'] = useLegacy;
  }

  // @ts-expect-error payload is loosely typed
  const { currency: _currency, ...validPayloads } = payload || {};
  const ats_equity_award_attributes = {
    amount,
    cliff_of_vesting,
    cycles_after_cliff,
    date_of_grant,
    period_of_vesting,
    start_date_of_vesting,
    strike_price,
    type_of_equity,
    unit_of_cliff,
    unit_of_cycle_after_cliff,
    unit_of_vesting_period,
    strike_price_currency_id,
    currency_id: award_type === 'money' ? currency_id : null,
  };

  return {
    ats_offer: {
      ats_application_id: applicationId,
      ats_equity_award_attributes,
      today_date:
        today_date && parseDateToGivenFormat(today_date, 'YYYY-MM-DD'),
      start_date:
        start_date && parseDateToGivenFormat(start_date, 'YYYY-MM-DD'),
      expiration_date:
        expiration_date &&
        parseDateToGivenFormat(expiration_date, 'YYYY-MM-DD'),
      ...validPayloads,
      // @ts-expect-error validPayloads is loosely typed
      req_id: validPayloads.requisitions_codes
        ? Array.isArray((validPayloads as any).requisitions_codes)
          ? (validPayloads as any).requisitions_codes[0]
          : (validPayloads as any).requisitions_codes
        : (validPayloads as any).req_id,
    },
  };
};

export const pollOfferWithRetries = async (
  pollOffer,
  pollId,
  maxRetries = 15,
  timeout = 3000
) => {
  let retries = 0;
  while (retries < maxRetries) {
    try {
      const response = await pollOffer({ id: pollId });
      if (response.data) {
        const status = response.data.offer_status;
        if (status === 'document_generated' || status === 'approved_internal') {
          return response.data;
        } else if (status === 'document_generation_error') {
          throw new Error('Document generation error');
        } else if (status !== 'pending_document_generation') {
          throw new Error('Unexpected status');
        }
      }
    } catch (err) {
      if (
        err.message === 'Document generation error' ||
        retries === maxRetries - 1
      ) {
        throw err;
      }
    }
    retries += 1;
    await new Promise((res) => setTimeout(res, timeout));
  }
  throw new Error('Maximum retries reached');
};

export const extractTokenArrays = ({ templateTokens, filledValues }) => {
  // Extract valid and invalid tokens from the template
  const validTokens = Object.keys(templateTokens).filter(
    (key) =>
      templateTokens[key] === 'valid' &&
      (key.startsWith('offer.') || key.startsWith('offer_cf.'))
  );

  const invalidTokens = Object.keys(templateTokens).filter(
    (key) =>
      templateTokens[key] === 'invalid' &&
      (key.startsWith('offer.') || key.startsWith('offer_cf.'))
  );

  // Extract keys in filledValues that are not in templateTokens but have a value
  const filledTokensNotInTemplate = Object.keys(filledValues || {}).filter(
    (key) => {
      return (
        !Object.prototype.hasOwnProperty.call(templateTokens, key) &&
        !key.startsWith('docusign') &&
        !key.startsWith('pandadocs') &&
        !key.startsWith('adobe') &&
        filledValues[key] !== null &&
        filledValues[key] !== undefined &&
        filledValues[key] !== ''
      );
    }
  );

  // Extract non-filled valid tokens (valid in template but not present in filledValues or having empty/null/undefined value)
  const nonFilledValidTokens = validTokens.filter((key) => {
    return (
      !Object.prototype.hasOwnProperty.call(filledValues, key) ||
      filledValues[key] === null ||
      filledValues[key] === undefined ||
      filledValues[key] === ''
    );
  });

  return {
    validTokens,
    invalidTokens,
    filledTokensNotInTemplate,
    nonFilledValidTokens,
  };
};

export const getFilledValues = ({
  detailsFormConfig,
  detailsFormRef,
  offerPayload = null,
}) => {
  const filledValues = {};
  const additionalCompensationFields =
    detailsFormConfig?.find((i) => i?.id === 'additionalCompensationSection')
      ?.fields || [];
  const offerDetailsFields =
    detailsFormConfig?.find((i) => i?.id === 'offerDetailsSection')?.fields ||
    [];
  const jobFields =
    detailsFormConfig?.find((i) => i?.id === 'jobDetailsSection')?.fields || [];
  const candidateFields =
    detailsFormConfig?.find((i) => i?.id === 'candidateSection')?.fields || [];
  const customFields =
    detailsFormConfig?.find((i) => i?.id === 'customFields')?.fields || [];

  // `|| undefined` would treat 0 / false / '' as "not filled" and surface the
  // "fields not filled for tokens" warning even though the user did enter 0
  // — keep only true emptiness (null / undefined / '') as missing.
  const pickIfFilled = (value: unknown) =>
    value !== '' && value !== null && value !== undefined ? value : undefined;

  additionalCompensationFields?.forEach((field) => {
    if (field.tokenName)
      filledValues[field.tokenName] = pickIfFilled(
        detailsFormRef?.current?.values[field.name]
      );
  });
  offerDetailsFields?.forEach((field) => {
    if (field.tokenName)
      filledValues[field.tokenName] = pickIfFilled(
        detailsFormRef?.current?.values[field.name]
      );
  });
  jobFields?.forEach((field) => {
    if (field.tokenName)
      filledValues[field.tokenName] = pickIfFilled(
        detailsFormRef?.current?.values[field.name]
      );
  });
  candidateFields?.forEach((field) => {
    if (field.tokenName)
      filledValues[field.tokenName] = pickIfFilled(
        detailsFormRef?.current?.values[field.name]
      );
  });
  const formValues =
    Object.keys(offerPayload || {})?.length > 0
      ? offerPayload
      : detailsFormRef?.current?.values;
  // For custom fields we additionally treat empty arrays / empty objects as
  // missing. Use the actual value (not `value || []`) for the type check so
  // that 0 / false don't fall back to a collection literal and get flagged.
  const isFilledCustomValue = (value: unknown) => {
    if (value === '' || value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
  };

  customFields?.forEach((field) => {
    if (field.tokenName) {
      const value = formValues[field.name];
      filledValues[field.tokenName] = isFilledCustomValue(value)
        ? value
        : undefined;
    }
  });

  return filledValues;
};

export const getButtonDisabled = ({
  tabIndex,
  detailsFormRef,
  templatesProps,
  detailsFormConfig,
  feOfferUploadReplacement,
}) => {
  if (!feOfferUploadReplacement) return false;
  if (tabIndex === 0) return false;
  if (tabIndex === 1) {
    if (!templatesProps?.selected_template_id) return true;
    if (templatesProps?.selected_template_id) {
      const { invalidTokens, nonFilledValidTokens } = extractTokenArrays({
        templateTokens: getUpdatedTokens(
          templatesProps?.template_value?.tokens
        ),
        filledValues: getFilledValues({ detailsFormConfig, detailsFormRef }),
      });
      if (invalidTokens?.length > 0) return true;
      if (nonFilledValidTokens?.length > 0) return true;
      return false;
    }
  }
};

export const getUpdatedTokens = (tokens) => {
  const mapping = {
    'offer.today_date': 'offer.offer_date',
    'offer.salary_frequency': 'offer.pay_type',
    'offer.additional_compensation': 'offer.additional_bonus',
    'offer.equity_cliff_of_vesting': 'offer.equity_cliff',
    'offer.equity_cycles_after_cliff': 'offer.equity_post_cliff_vesting_cycle',
  };

  const updatedTokens = {};

  for (const key in tokens) {
    if (mapping[key]) {
      updatedTokens[mapping[key]] = tokens[key];
    } else if (!Object.values(mapping).includes(key)) {
      updatedTokens[key] = tokens[key];
    }
  }

  return updatedTokens;
};
